import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, Lock, EyeOff, UserCheck, ChevronLeft, Search,
  CheckCircle2, Copy, Check, Globe, CalendarClock, Scale,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CODES = [
  'harassment', 'discrimination', 'fraud', 'conflict_of_interest',
  'inappropriate_conduct', 'policy_compliance', 'safety', 'other',
];

const STATUS_STYLES = {
  received: 'bg-sky-50 text-sky-700 border-sky-200',
  under_hm_review: 'bg-amber-50 text-amber-700 border-amber-200',
  routed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed_admin: 'bg-slate-100 text-slate-600 border-slate-200',
};

function Disclaimer({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8 border-t pt-4" data-testid="ge-disclaimer">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="ge-disclaimer-toggle">
        <Scale className="h-3.5 w-3.5" />
        {t('globalEthics.disclaimer.title')}
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          {t('globalEthics.disclaimer.pendingTag')}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground" data-testid="ge-disclaimer-body">
          {['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].map((k) => (
            <p key={k}>{t(`globalEthics.disclaimer.${k}`)}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function TrustPoints({ t }) {
  const points = [
    { icon: EyeOff, key: 'point1' },
    { icon: Lock, key: 'point2' },
    { icon: UserCheck, key: 'point3' },
  ];
  return (
    <ul className="space-y-2.5 mt-6" data-testid="ge-trust-points">
      {points.map(({ icon: Icon, key }) => (
        <li key={key} className="flex items-start gap-2.5 text-base text-muted-foreground">
          <Icon className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          {t(`globalEthics.access.${key}`)}
        </li>
      ))}
    </ul>
  );
}

export default function GlobalEthicsPublic() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState('access'); // access | form | done | status
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [code, setCode] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [additional, setAdditional] = useState('');
  const [identify, setIdentify] = useState('no');
  const [reporterName, setReporterName] = useState('');
  const [reporterContact, setReporterContact] = useState('');
  const [meeting, setMeeting] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  const [reference, setReference] = useState('');
  const [copied, setCopied] = useState(false);

  const [statusRef, setStatusRef] = useState('');
  const [statusResult, setStatusResult] = useState(null);

  const apiError = (e) => {
    if (e?.response?.status === 429) return t('globalEthics.errors.rateLimited');
    if (e?.response?.status === 404) return null;
    return t('globalEthics.errors.generic');
  };

  const verifyCode = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/ethics/access`, { company_code: code.trim() });
      setCompanyName(data.company_name);
      setStep('form');
    } catch (e) {
      setError(apiError(e) || t('globalEthics.access.invalidCode'));
    } finally { setBusy(false); }
  };

  const submitReport = async () => {
    if (!category) { setError(t('globalEthics.form.errCategory')); return; }
    if (description.trim().length < 10) { setError(t('globalEthics.form.errDescription')); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/ethics/reports`, {
        company_code: code.trim(),
        category,
        description: description.trim(),
        additional_info: additional.trim() || null,
        identify: identify === 'yes',
        reporter_name: identify === 'yes' ? reporterName.trim() || null : null,
        reporter_contact: identify === 'yes' ? reporterContact.trim() || null : null,
        meeting_requested: meeting,
        website: honeypot || null,
      });
      setReference(data.reference_number);
      setStep('done');
      window.scrollTo(0, 0);
    } catch (e) {
      setError(apiError(e) || t('globalEthics.errors.generic'));
    } finally { setBusy(false); }
  };

  const checkStatus = async () => {
    setBusy(true); setError(null); setStatusResult(null);
    try {
      const { data } = await axios.post(`${API}/ethics/status`, {
        company_code: code.trim(), reference_number: statusRef.trim(),
      });
      setStatusResult(data);
    } catch (e) {
      setError(apiError(e) || t('globalEthics.status.notFound'));
    } finally { setBusy(false); }
  };

  const copyRef = async () => {
    try { await navigator.clipboard.writeText(reference); } catch { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const anonWithMeeting = useMemo(
    () => meeting && identify === 'no',
    [meeting, identify],
  );

  const toggleLang = () => i18n.changeLanguage(i18n.language?.startsWith('es') ? 'en' : 'es');

  return (
    <div className="min-h-screen bg-[hsl(210,25%,97%)] flex flex-col" data-testid="global-ethics-page">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-2xl lg:max-w-[880px] mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold leading-tight" data-testid="ge-brand">{t('globalEthics.brand')}</p>
              <p className="text-sm text-muted-foreground leading-tight">{t('globalEthics.byline')}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={toggleLang} className="gap-1.5 text-xs"
                  data-testid="ge-lang-toggle">
            <Globe className="h-3.5 w-3.5" />
            {t('globalEthics.langToggle')}
          </Button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl lg:max-w-[880px] mx-auto px-5 py-8 sm:py-10">
        {/* STEP: access */}
        {step === 'access' && (
          <section className="bg-white border rounded-2xl shadow-sm p-6 sm:p-8 lg:p-10" data-testid="ge-access-step">
            <div className="lg:grid lg:grid-cols-[1.15fr_1fr] lg:gap-12 lg:items-start">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{t('globalEthics.access.title')}</h1>
                <p className="text-base text-muted-foreground mt-2 leading-relaxed">{t('globalEthics.access.subtitle')}</p>
                <TrustPoints t={t} />
              </div>
              <div className="mt-7 lg:mt-0 lg:rounded-xl lg:border lg:bg-[hsl(210,25%,98%)] lg:p-6">
                <div className="space-y-2">
                  <Label htmlFor="ge-code">{t('globalEthics.access.codeLabel')}</Label>
                  <Input id="ge-code" value={code} onChange={(e) => setCode(e.target.value)}
                         placeholder={t('globalEthics.access.codePlaceholder')}
                         className="uppercase tracking-wide bg-white md:text-base"
                         onKeyDown={(e) => e.key === 'Enter' && code.trim() && verifyCode()}
                         data-testid="ge-code-input" />
                  <p className="text-sm text-muted-foreground">{t('globalEthics.access.codeHint')}</p>
                </div>
                {error && <p className="text-sm text-destructive mt-3" data-testid="ge-access-error">{error}</p>}
                <Button className="w-full mt-5 rounded-full" disabled={!code.trim() || busy}
                        onClick={verifyCode} data-testid="ge-continue-btn">
                  {busy ? t('globalEthics.access.checking') : t('globalEthics.access.continue')}
                </Button>
                <button type="button"
                        className="mt-4 w-full text-center text-sm text-primary hover:underline inline-flex items-center justify-center gap-1.5"
                        onClick={() => { setError(null); setStep('status'); }}
                        data-testid="ge-status-link">
                  <Search className="h-3.5 w-3.5" />
                  {t('globalEthics.access.statusLink')}
                </button>
              </div>
            </div>
            <Disclaimer t={t} />
          </section>
        )}

        {/* STEP: form */}
        {step === 'form' && (
          <section className="bg-white border rounded-2xl shadow-sm p-6 sm:p-8 lg:p-10" data-testid="ge-form-step">
            <button type="button" onClick={() => { setError(null); setStep('access'); }}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
                    data-testid="ge-back-btn">
              <ChevronLeft className="h-3.5 w-3.5" /> {t('globalEthics.form.back')}
            </button>
            <h1 className="text-2xl font-semibold tracking-tight">{t('globalEthics.form.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('globalEthics.form.reportingTo')}{' '}
              <span className="font-medium text-foreground" data-testid="ge-company-name">{companyName}</span>
            </p>

            <div className="mt-6 space-y-6">
              <div className="space-y-2">
                <Label>{t('globalEthics.form.categoryLabel')}</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="text-base" data-testid="ge-category-select">
                    <SelectValue placeholder={t('globalEthics.form.categoryPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_CODES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`ge-category-${c}`}>
                        {t(`globalEthics.categories.${c}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ge-desc">{t('globalEthics.form.descriptionLabel')}</Label>
                <Textarea id="ge-desc" rows={6} className="md:text-base" value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder={t('globalEthics.form.descriptionPlaceholder')}
                          data-testid="ge-description-input" />
                <p className="text-sm text-muted-foreground">{t('globalEthics.form.descriptionHint')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ge-add">{t('globalEthics.form.additionalLabel')}</Label>
                <Textarea id="ge-add" rows={3} className="md:text-base" value={additional}
                          onChange={(e) => setAdditional(e.target.value)}
                          placeholder={t('globalEthics.form.additionalPlaceholder')}
                          data-testid="ge-additional-input" />
              </div>

              {/* Honeypot — invisible to humans */}
              <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                     tabIndex={-1} autoComplete="off" aria-hidden="true"
                     style={{ position: 'absolute', left: '-5000px', height: 0, width: 0, opacity: 0 }}
                     name="website" data-testid="ge-honeypot" />

              <div className="space-y-3 rounded-xl border bg-[hsl(210,25%,98%)] p-4">
                <Label className="flex items-center gap-2">
                  <EyeOff className="h-4 w-4 text-primary" />
                  {t('globalEthics.form.identityLabel')}
                </Label>
                <RadioGroup value={identify} onValueChange={setIdentify} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="no" id="ge-anon" data-testid="ge-identity-no" />
                    <Label htmlFor="ge-anon" className="text-base font-normal cursor-pointer">
                      {t('globalEthics.form.identityAnon')}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="yes" id="ge-ident" data-testid="ge-identity-yes" />
                    <Label htmlFor="ge-ident" className="text-base font-normal cursor-pointer">
                      {t('globalEthics.form.identityYes')}
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-sm text-muted-foreground">{t('globalEthics.form.identityNote')}</p>
                {identify === 'yes' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1" data-testid="ge-identity-fields">
                    <div className="space-y-1.5">
                      <Label htmlFor="ge-name" className="text-sm">{t('globalEthics.form.nameLabel')}</Label>
                      <Input id="ge-name" className="md:text-base" value={reporterName}
                             onChange={(e) => setReporterName(e.target.value)}
                             data-testid="ge-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ge-contact" className="text-sm">{t('globalEthics.form.contactLabel')}</Label>
                      <Input id="ge-contact" className="md:text-base" value={reporterContact}
                             onChange={(e) => setReporterContact(e.target.value)}
                             data-testid="ge-contact-input" />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-start gap-2.5">
                  <Checkbox id="ge-meeting" checked={meeting}
                            onCheckedChange={(v) => setMeeting(Boolean(v))}
                            data-testid="ge-meeting-checkbox" />
                  <Label htmlFor="ge-meeting" className="text-base font-normal cursor-pointer flex items-center gap-1.5">
                    <CalendarClock className="h-4 w-4 text-primary" />
                    {t('globalEthics.form.meetingLabel')}
                  </Label>
                </div>
                {anonWithMeeting && (
                  <p className="text-sm leading-relaxed rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-3"
                     data-testid="ge-meeting-anon-note">
                    {t('globalEthics.form.meetingAnonNote')}
                  </p>
                )}
              </div>

              {error && <p className="text-sm text-destructive" data-testid="ge-form-error">{error}</p>}
              <Button className="w-full rounded-full" disabled={busy}
                      onClick={submitReport} data-testid="ge-submit-btn">
                {busy ? t('globalEthics.form.submitting') : t('globalEthics.form.submit')}
              </Button>
            </div>
            <Disclaimer t={t} />
          </section>
        )}

        {/* STEP: done */}
        {step === 'done' && (
          <section className="bg-white border rounded-2xl shadow-sm p-6 sm:p-8 text-center lg:max-w-2xl lg:mx-auto" data-testid="ge-done-step">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight mt-4">{t('globalEthics.done.title')}</h1>
            <p className="text-base text-muted-foreground mt-2 leading-relaxed">{t('globalEthics.done.message')}</p>
            <div className="mt-6 rounded-xl border bg-[hsl(210,25%,98%)] p-4">
              <p className="text-sm text-muted-foreground uppercase tracking-wider">{t('globalEthics.done.referenceLabel')}</p>
              <p className="text-2xl font-semibold tracking-widest mt-1 tabular-nums" data-testid="ge-reference-number">{reference}</p>
              <Button variant="outline" size="sm" className="mt-3 rounded-full gap-1.5"
                      onClick={copyRef} data-testid="ge-copy-reference-btn">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t('globalEthics.done.copied') : t('globalEthics.done.copy')}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-3">{t('globalEthics.done.saveNote')}</p>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1 rounded-full"
                      onClick={() => { setStatusRef(reference); setStatusResult(null); setError(null); setStep('status'); }}
                      data-testid="ge-done-status-btn">
                {t('globalEthics.done.statusCta')}
              </Button>
              <Button className="flex-1 rounded-full"
                      onClick={() => window.location.reload()}
                      data-testid="ge-done-finish-btn">
                {t('globalEthics.done.finish')}
              </Button>
            </div>
            <Disclaimer t={t} />
          </section>
        )}

        {/* STEP: status */}
        {step === 'status' && (
          <section className="bg-white border rounded-2xl shadow-sm p-6 sm:p-8 lg:max-w-2xl lg:mx-auto" data-testid="ge-status-step">
            <button type="button" onClick={() => { setError(null); setStep('access'); }}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
                    data-testid="ge-status-back-btn">
              <ChevronLeft className="h-3.5 w-3.5" /> {t('globalEthics.status.back')}
            </button>
            <h1 className="text-2xl font-semibold tracking-tight">{t('globalEthics.status.title')}</h1>
            <p className="text-base text-muted-foreground mt-1">{t('globalEthics.status.subtitle')}</p>
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ge-status-code">{t('globalEthics.access.codeLabel')}</Label>
                  <Input id="ge-status-code" value={code} onChange={(e) => setCode(e.target.value)}
                         placeholder={t('globalEthics.access.codePlaceholder')}
                         className="uppercase tracking-wide md:text-base" data-testid="ge-status-code-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ge-status-ref">{t('globalEthics.status.refLabel')}</Label>
                  <Input id="ge-status-ref" value={statusRef} onChange={(e) => setStatusRef(e.target.value)}
                         placeholder={t('globalEthics.status.refPlaceholder')}
                         className="uppercase tracking-wide md:text-base" data-testid="ge-status-ref-input" />
                </div>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="ge-status-error">{error}</p>}
              <Button className="w-full rounded-full" disabled={!code.trim() || !statusRef.trim() || busy}
                      onClick={checkStatus} data-testid="ge-status-check-btn">
                {busy ? t('globalEthics.status.checking') : t('globalEthics.status.check')}
              </Button>
              {statusResult && (
                <div className="rounded-xl border p-4 text-center" data-testid="ge-status-result">
                  <span className={`inline-block text-sm font-medium px-3 py-1 rounded-full border ${STATUS_STYLES[statusResult.status] || 'bg-muted'}`}
                        data-testid="ge-status-value">
                    {t(`globalEthics.status.statuses.${statusResult.status}`)}
                  </span>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('globalEthics.status.submittedOn')} {statusResult.submitted_at}
                  </p>
                  {statusResult.status === 'closed_admin' && (
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {t('globalEthics.status.closedNote')}
                    </p>
                  )}
                </div>
              )}
            </div>
            <Disclaimer t={t} />
          </section>
        )}
      </main>

      <footer className="py-6 text-center text-sm text-muted-foreground">
        {t('globalEthics.brand')} · {t('globalEthics.byline')}
      </footer>
    </div>
  );
}
